import type { ReactNode } from 'react';

import type { IconName } from '../icon/generated_icons';
import {
  EmptyStateActionButton,
  type EmptyStateActionButtonProps,
} from './empty_state_action_button';
import {
  EmptyStateActionLink,
  type EmptyStateActionLinkProps,
} from './empty_state_action_link';
import {
  EmptyStateContainer,
  type EmptyStateContainerProps,
} from './empty_state_container';
import { EmptyStateHeading } from './empty_state_heading';
import { EmptyStateIcon } from './empty_state_icon';
import { EmptyStateImage } from './empty_state_image';
import { EmptyStateText } from './empty_state_text';

export type EmptyStateProps = EmptyStateContainerProps & {
  title?: ReactNode;
  description?: ReactNode;
  /** Ignored when `imageSrc` is set. */
  iconName?: IconName;
  /** A custom illustration, such as an SVG imported as a URL. Takes precedence over `iconName`. */
  imageSrc?: string;
  /** Leave unset for decorative illustrations. */
  imageAlt?: string;
  /** Required for `onAction` or `actionHref` to render anything. */
  actionButtonLabel?: ReactNode;
  /** With `actionHref`, runs in addition to navigating rather than instead. */
  onAction?: EmptyStateActionButtonProps['onPress'];
  /** Renders the action as an `<a href>` instead of a `<button>`. */
  actionHref?: EmptyStateActionLinkProps['href'];
  /** Limited to what a button and a link both accept. */
  actionButtonProps?: Omit<
    EmptyStateActionButtonProps,
    'children' | 'onPress' | 'variant'
  > &
    Omit<
      EmptyStateActionLinkProps,
      'children' | 'href' | 'onPress' | 'variant'
    >;
};

export function EmptyState(props: EmptyStateProps) {
  const {
    title,
    description,
    actionButtonLabel,
    iconName,
    imageSrc,
    imageAlt,
    onAction,
    actionHref,
    actionButtonProps,
    ...rest
  } = props;

  return (
    <EmptyStateContainer {...rest}>
      {imageSrc ? (
        <EmptyStateImage src={imageSrc} alt={imageAlt} />
      ) : iconName ? (
        <EmptyStateIcon name={iconName} />
      ) : null}
      {title ? <EmptyStateHeading>{title}</EmptyStateHeading> : null}
      {description ? <EmptyStateText>{description}</EmptyStateText> : null}
      {actionButtonLabel ? (
        actionHref ? (
          <EmptyStateActionLink
            href={actionHref}
            onPress={onAction}
            {...actionButtonProps}
          >
            {actionButtonLabel}
          </EmptyStateActionLink>
        ) : (
          <EmptyStateActionButton onPress={onAction} {...actionButtonProps}>
            {actionButtonLabel}
          </EmptyStateActionButton>
        )
      ) : null}
    </EmptyStateContainer>
  );
}
